import {
  type NamespacedMessages,
  type PresentingLocale,
} from '@mentimeter/i18n-core';
import deepmerge from 'deepmerge';
import caES from './ca-ES.json' assert { type: 'json' };
import chHansZN from './ch-Hans-ZN.json' assert { type: 'json' };
import csCZ from './cs-CZ.json' assert { type: 'json' };
import cyGB from './cy-GB.json' assert { type: 'json' };
import daDK from './da-DK.json' assert { type: 'json' };
import deDE from './de-DE.json' assert { type: 'json' };
import en from './en.json' assert { type: 'json' };
import esES from './es-ES.json' assert { type: 'json' };
import etEE from './et-EE.json' assert { type: 'json' };
import fiFI from './fi-FI.json' assert { type: 'json' };
import frFR from './fr-FR.json' assert { type: 'json' };
import hiIn from './hi-IN.json' assert { type: 'json' };
import hrHR from './hr-HR.json' assert { type: 'json' };
import huHU from './hu-HU.json' assert { type: 'json' };
import idID from './id-ID.json' assert { type: 'json' };
import isIS from './is-IS.json' assert { type: 'json' };
import itIT from './it-IT.json' assert { type: 'json' };
import jaJA from './ja-JA.json' assert { type: 'json' };
import ltLT from './lt-LT.json' assert { type: 'json' };
import nlNL from './nl-NL.json' assert { type: 'json' };
import noNO from './no-NO.json' assert { type: 'json' };
import paPK from './pa-PK.json' assert { type: 'json' };
import plPL from './pl-PL.json' assert { type: 'json' };
import ptBR from './pt-BR.json' assert { type: 'json' };
import ptPT from './pt-PT.json' assert { type: 'json' };
import roRO from './ro-RO.json' assert { type: 'json' };
import ruRU from './ru-RU.json' assert { type: 'json' };
import slSL from './sl-SL.json' assert { type: 'json' };
import srLatnRS from './sr-Latn-RS.json' assert { type: 'json' };
import svSE from './sv-SE.json' assert { type: 'json' };
import taIN from './ta-IN.json' assert { type: 'json' };
import trTR from './tr-TR.json' assert { type: 'json' };
import ukUA from './uk-UA.json' assert { type: 'json' };
import caESUnsynced from './unsynced/ca-ES.json' assert { type: 'json' };
import chHansZNUnsynced from './unsynced/ch-Hans-ZN.json' assert {
  type: 'json',
};
import csCZUnsynced from './unsynced/cs-CZ.json' assert { type: 'json' };
import cyGBUnsynced from './unsynced/cy-GB.json' assert { type: 'json' };
import daDKUnsynced from './unsynced/da-DK.json' assert { type: 'json' };
import deDEUnsynced from './unsynced/de-DE.json' assert { type: 'json' };
import esESUnsynced from './unsynced/es-ES.json' assert { type: 'json' };
import etEEUnsynced from './unsynced/et-EE.json' assert { type: 'json' };
import fiFIUnsynced from './unsynced/fi-FI.json' assert { type: 'json' };
import frFRUnsynced from './unsynced/fr-FR.json' assert { type: 'json' };
import hiInUnsynced from './unsynced/hi-IN.json' assert { type: 'json' };
import hrHRUnsynced from './unsynced/hr-HR.json' assert { type: 'json' };
import huHUUnsynced from './unsynced/hu-HU.json' assert { type: 'json' };
import idIDUnsynced from './unsynced/id-ID.json' assert { type: 'json' };
import isISUnsynced from './unsynced/is-IS.json' assert { type: 'json' };
import itITUnsynced from './unsynced/it-IT.json' assert { type: 'json' };
import jaJAUnsynced from './unsynced/ja-JA.json' assert { type: 'json' };
import ltLTUnsynced from './unsynced/lt-LT.json' assert { type: 'json' };
import nlNLUnsynced from './unsynced/nl-NL.json' assert { type: 'json' };
import noNOUnsynced from './unsynced/no-NO.json' assert { type: 'json' };
import paPKUnsynced from './unsynced/pa-PK.json' assert { type: 'json' };
import plPLUnsynced from './unsynced/pl-PL.json' assert { type: 'json' };
import ptBRUnsynced from './unsynced/pt-BR.json' assert { type: 'json' };
import ptPTUnsynced from './unsynced/pt-PT.json' assert { type: 'json' };
import roROUnsynced from './unsynced/ro-RO.json' assert { type: 'json' };
import ruRUUnsynced from './unsynced/ru-RU.json' assert { type: 'json' };
import slSLUnsynced from './unsynced/sl-SL.json' assert { type: 'json' };
import srLatnRSUnsynced from './unsynced/sr-Latn-RS.json' assert {
  type: 'json',
};
import svSEUnsynced from './unsynced/sv-SE.json' assert { type: 'json' };
import taINUnsynced from './unsynced/ta-IN.json' assert { type: 'json' };
import trTRUnsynced from './unsynced/tr-TR.json' assert { type: 'json' };
import ukUAUnsynced from './unsynced/uk-UA.json' assert { type: 'json' };
import zhCNUnsynced from './unsynced/zh-CN.json' assert { type: 'json' };
import zhCN from './zh-CN.json' assert { type: 'json' };

const ROOT_KEY = 'participating' as const;
type RootKey = typeof ROOT_KEY;
const defaultLanguage: NamespacedMessages<RootKey> = en;

const unsynced: Omit<NamespacedMessages<PresentingLocale>, 'en'> = {
  'ca-ES': caESUnsynced,
  'ch-Hans-ZN': chHansZNUnsynced,
  'cs-CZ': csCZUnsynced,
  'cy-GB': cyGBUnsynced,
  'da-DK': daDKUnsynced,
  'de-DE': deDEUnsynced,
  'es-ES': esESUnsynced,
  'et-EE': etEEUnsynced,
  'fi-FI': fiFIUnsynced,
  'fr-FR': frFRUnsynced,
  'hi-IN': hiInUnsynced,
  'hr-HR': hrHRUnsynced,
  'hu-HU': huHUUnsynced,
  'id-ID': idIDUnsynced,
  'is-IS': isISUnsynced,
  'it-IT': itITUnsynced,
  'ja-JA': jaJAUnsynced,
  'lt-LT': ltLTUnsynced,
  'nl-NL': nlNLUnsynced,
  'no-NO': noNOUnsynced,
  'pa-PK': paPKUnsynced,
  'pl-PL': plPLUnsynced,
  'pt-BR': ptBRUnsynced,
  'pt-PT': ptPTUnsynced,
  'ro-RO': roROUnsynced,
  'ru-RU': ruRUUnsynced,
  'sl-SL': slSLUnsynced,
  'sr-Latn-RS': srLatnRSUnsynced,
  'sv-SE': svSEUnsynced,
  'ta-IN': taINUnsynced,
  'tr-TR': trTRUnsynced,
  'uk-UA': ukUAUnsynced,
  'zh-CN': zhCNUnsynced,
};

const synced: NamespacedMessages<PresentingLocale> = {
  'ca-ES': caES,
  'ch-Hans-ZN': chHansZN,
  'cs-CZ': csCZ,
  'cy-GB': cyGB,
  'da-DK': daDK,
  'de-DE': deDE,
  en: defaultLanguage,
  'es-ES': esES,
  'et-EE': etEE,
  'fi-FI': fiFI,
  'fr-FR': frFR,
  'hi-IN': hiIn,
  'hr-HR': hrHR,
  'hu-HU': huHU,
  'id-ID': idID,
  'is-IS': isIS,
  'it-IT': itIT,
  'ja-JA': jaJA,
  'lt-LT': ltLT,
  'nl-NL': nlNL,
  'no-NO': noNO,
  'pa-PK': paPK,
  'pl-PL': plPL,
  'pt-BR': ptBR,
  'pt-PT': ptPT,
  'ro-RO': roRO,
  'ru-RU': ruRU,
  'sl-SL': slSL,
  'sr-Latn-RS': srLatnRS,
  'sv-SE': svSE,
  'ta-IN': taIN,
  'tr-TR': trTR,
  'uk-UA': ukUA,
  'zh-CN': zhCN,
};

export const messages = deepmerge(
  unsynced,
  synced,
) as NamespacedMessages<PresentingLocale>;
export type Messages = typeof en;
